import React, { useMemo, useState } from 'react';

import classNames from 'classnames';
import { ExternalLink } from 'lucide-react';
import { useQuery } from 'react-query';

import { fetchCallModulesForAccount, fetchIvrSelectionsSummary } from '../api';
import type { CallModuleSummary } from '../api';

const LANG_LABELS: Record<string, string> = {
  es: 'Español',
  en: 'English',
};

const langLabel = (code: string | null): string => {
  if (!code) return 'Sin selección';
  return LANG_LABELS[code.toLowerCase()] ?? code.toUpperCase();
};

const LANG_COLORS: Record<string, string> = {
  es: 'bg-blue-500',
  en: 'bg-emerald-500',
  __none__: 'bg-gray-300',
};

const langColor = (code: string | null): string => {
  if (!code) return LANG_COLORS.__none__;
  return LANG_COLORS[code.toLowerCase()] ?? 'bg-purple-500';
};

const toIsoDate = (date: Date): string => date.toISOString().slice(0, 10);

interface RangePreset {
  id: string;
  label: string;
  compute: () => { dateFrom: string; dateTo: string };
}

const RANGE_PRESETS: RangePreset[] = [
  {
    id: 'today',
    label: 'Hoy',
    compute: () => {
      const today = toIsoDate(new Date());
      return { dateFrom: today, dateTo: today };
    },
  },
  {
    id: 'yesterday',
    label: 'Ayer',
    compute: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const iso = toIsoDate(yesterday);
      return { dateFrom: iso, dateTo: iso };
    },
  },
  {
    id: '7d',
    label: 'Últimos 7 días',
    compute: () => {
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - 6);
      return { dateFrom: toIsoDate(from), dateTo: toIsoDate(to) };
    },
  },
  {
    id: '30d',
    label: 'Últimos 30 días',
    compute: () => {
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - 29);
      return { dateFrom: toIsoDate(from), dateTo: toIsoDate(to) };
    },
  },
  {
    id: '90d',
    label: 'Últimos 3 meses',
    compute: () => {
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - 89);
      return { dateFrom: toIsoDate(from), dateTo: toIsoDate(to) };
    },
  },
];

const openStandaloneWindow = (accountId: number): void => {
  const standaloneUrl = `${window.location.origin}/app/phone-settings/tabulations/${accountId}`;
  window.open(
    standaloneUrl,
    'TabulationsDashboard',
    'width=1100,height=700,menubar=no,toolbar=no,location=no,status=no'
  );
};

interface TabulationsDashboardProps {
  accountId: number;
  isStandalone?: boolean;
}

export const TabulationsDashboard: React.FC<TabulationsDashboardProps> = ({
  accountId,
  isStandalone = false,
}) => {
  const [moduleId, setModuleId] = useState<number | ''>('');
  const [rangePreset, setRangePreset] = useState<string>('90d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const dateRange = useMemo(() => {
    if (rangePreset === 'custom') {
      return { dateFrom: customFrom, dateTo: customTo };
    }
    const preset = RANGE_PRESETS.find((p) => p.id === rangePreset);
    if (!preset) return { dateFrom: '', dateTo: '' };
    return preset.compute();
  }, [rangePreset, customFrom, customTo]);

  const { data: modules = [], isLoading: modulesLoading } = useQuery<CallModuleSummary[], Error>(
    ['ivr-tabulations-modules', accountId],
    () => fetchCallModulesForAccount(accountId),
    { refetchOnWindowFocus: false, retry: 1 }
  );

  const summaryQueryKey = useMemo(
    () =>
      [
        'ivr-tabulations-summary',
        accountId,
        moduleId,
        dateRange.dateFrom,
        dateRange.dateTo,
      ] as const,
    [accountId, moduleId, dateRange.dateFrom, dateRange.dateTo]
  );

  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
  } = useQuery(
    summaryQueryKey,
    () =>
      fetchIvrSelectionsSummary(accountId, {
        ...(moduleId !== '' ? { moduleId: Number(moduleId) } : {}),
        ...(dateRange.dateFrom ? { dateFrom: dateRange.dateFrom } : {}),
        ...(dateRange.dateTo ? { dateTo: dateRange.dateTo } : {}),
      }),
    { refetchOnWindowFocus: false, retry: 1, keepPreviousData: true }
  );

  const totalCalls = summary?.total_calls ?? 0;

  const choiceData = useMemo(() => {
    const buckets = summary?.buckets ?? [];
    const map = new Map<
      string,
      { choice: string; total: number; byLang: Record<string, number> }
    >();
    buckets.forEach((b) => {
      if (!b.ivr_choice) return;
      const key = b.ivr_choice;
      if (!map.has(key)) {
        map.set(key, { choice: key, total: 0, byLang: {} });
      }
      const entry = map.get(key);
      if (!entry) return;
      entry.total += b.count;
      const langKey = b.ivr_lang ?? '__none__';
      entry.byLang[langKey] = (entry.byLang[langKey] ?? 0) + b.count;
    });
    return Array.from(map.values()).sort((a, b) => {
      const an = Number(a.choice);
      const bn = Number(b.choice);
      if (Number.isNaN(an) || Number.isNaN(bn)) return a.choice.localeCompare(b.choice);
      return an - bn;
    });
  }, [summary]);

  const maxChoiceCount = useMemo(
    () => choiceData.reduce((max, c) => (c.total > max ? c.total : max), 0),
    [choiceData]
  );

  const usedLanguages = useMemo(() => {
    const set = new Set<string>();
    choiceData.forEach((c) => Object.keys(c.byLang).forEach((k) => set.add(k)));
    return Array.from(set).sort();
  }, [choiceData]);

  const rangeNote = useMemo(() => {
    if (rangePreset === 'custom') {
      if (dateRange.dateFrom && dateRange.dateTo) {
        return `${dateRange.dateFrom} → ${dateRange.dateTo}`;
      }
      return 'Sin fechas, últimos 90 días';
    }
    const preset = RANGE_PRESETS.find((p) => p.id === rangePreset);
    return preset ? preset.label : 'Sin fechas, últimos 90 días';
  }, [rangePreset, dateRange.dateFrom, dateRange.dateTo]);

  return (
    <div
      className={classNames(
        'px-4 py-4 md:px-6 md:py-6',
        isStandalone
          ? 'min-h-screen bg-gray-50'
          : 'flex min-h-0 flex-1 flex-col overflow-y-auto bg-gray-50'
      )}
    >
      {/* Header con título + filtros */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h2 className="md:text-2xl text-xl font-bold text-gray-900">Tabulaciones de IVR</h2>
          <p className="mt-1 text-sm text-gray-500">
            Cuenta #{accountId} · Selecciones de idioma y opción presionada por los llamantes.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex p-1 bg-gray-100 rounded-lg">
            {RANGE_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setRangePreset(p.id)}
                className={classNames(
                  'px-3 py-1.5 text-xs font-semibold rounded-md transition-colors',
                  rangePreset === p.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                {p.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setRangePreset('custom')}
              className={classNames(
                'px-3 py-1.5 text-xs font-semibold rounded-md transition-colors',
                rangePreset === 'custom'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              Personalizado
            </button>
          </div>

          <select
            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
            value={moduleId}
            onChange={(e) => setModuleId(e.target.value === '' ? '' : Number(e.target.value))}
            disabled={modulesLoading}
            aria-label="Módulo"
          >
            <option value="">Todos los módulos</option>
            {modules.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
                {m.phone_number ? ` · ${m.phone_number}` : ''}
              </option>
            ))}
          </select>

          {!isStandalone && (
            <button
              type="button"
              onClick={() => openStandaloneWindow(accountId)}
              className="hover:text-gray-600 text-gray-400"
              title="Abrir en nueva ventana"
            >
              <ExternalLink size={20} />
            </button>
          )}
        </div>
      </div>

      {rangePreset === 'custom' && (
        <div className="flex flex-wrap items-center justify-end gap-3 mb-4">
          <label className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Desde
            </span>
            <input
              type="date"
              className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={customFrom}
              max={customTo || undefined}
              onChange={(e) => setCustomFrom(e.target.value)}
            />
          </label>
          <label className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Hasta
            </span>
            <input
              type="date"
              className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={customTo}
              min={customFrom || undefined}
              onChange={(e) => setCustomTo(e.target.value)}
            />
          </label>
        </div>
      )}

      <div className="rounded-xl bg-white border border-gray-200 shadow-sm">
        {/* Total + leyenda */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-gray-100">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Llamadas totales
            </p>
            <p className="tabular-nums text-3xl font-bold text-gray-900">{totalCalls}</p>
            <p className="mt-0.5 text-[11px] text-gray-400">{rangeNote}</p>
          </div>
          {usedLanguages.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              {usedLanguages.map((lang) => (
                <div key={lang} className="flex items-center gap-1.5">
                  <span
                    className={classNames(
                      'inline-block h-3 w-3 rounded-sm',
                      langColor(lang === '__none__' ? null : lang)
                    )}
                  />
                  <span className="text-xs font-medium text-gray-700">
                    {langLabel(lang === '__none__' ? null : lang)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Gráfica */}
        <div className="px-6 py-6">
          {summaryLoading && (
            <p className="py-12 text-sm text-center text-gray-400">Calculando tabulaciones…</p>
          )}
          {summaryError && !summaryLoading && (
            <p className="py-12 text-sm text-center text-red-500">
              No se pudieron cargar las tabulaciones. Verifica que el dialplan esté registrando
              `ivr_lang` / `ivr_choice`.
            </p>
          )}
          {!summaryLoading && !summaryError && choiceData.length === 0 && (
            <p className="py-12 text-sm text-center text-gray-500">
              No hay tabulaciones registradas para los filtros seleccionados.
            </p>
          )}
          {!summaryLoading && !summaryError && choiceData.length > 0 && (
            <div className="space-y-4">
              {choiceData.map((c) => {
                const pct = totalCalls > 0 ? Math.round((c.total / totalCalls) * 100) : 0;
                const widthPct =
                  maxChoiceCount > 0 ? Math.round((c.total / maxChoiceCount) * 100) : 0;
                return (
                  <div key={c.choice} className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-16 text-right">
                      <span className="font-mono text-lg font-bold text-gray-900">{c.choice}</span>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        Opción
                      </p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className="relative h-8 overflow-hidden bg-gray-100 rounded"
                        style={{ width: `${widthPct}%`, minWidth: '40px' }}
                      >
                        <div className="absolute inset-0 flex">
                          {usedLanguages.map((lang) => {
                            const langCount = c.byLang[lang] ?? 0;
                            if (langCount === 0) return null;
                            const langPctOfBar = c.total > 0 ? (langCount / c.total) * 100 : 0;
                            return (
                              <div
                                key={lang}
                                className={classNames(
                                  'h-full transition-all',
                                  langColor(lang === '__none__' ? null : lang)
                                )}
                                style={{ width: `${langPctOfBar}%` }}
                                title={`${langLabel(
                                  lang === '__none__' ? null : lang
                                )}: ${langCount}`}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 w-24 text-right">
                      <p className="tabular-nums text-base font-bold text-gray-900">{c.total}</p>
                      <p className="text-[11px] font-medium text-gray-400 tabular-nums">{pct}%</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
